import { createBrowserRouter, Navigate } from "react-router";
import EquipmentPage from "./routes/equipment";
import EquipmentDetailPage from "./routes/equipment-detail";
import EventsPage from "./routes/events";
import EventWorkspacePage from "./routes/event-workspace";
import LoginPage from "./routes/login";
import NotFound from "./routes/not-found";
import SchedulePage from "./routes/schedule";
import SettingsPage from "./routes/settings";
import XeroCallbackPage from "./routes/xero-callback";
import { AppShell } from "./components/ui/AppShell";

/**
 * Admin SPA route config.
 *
 * The SPA is hosted at the admin origin root, so legacy `/admin/*` paths map
 * to `/*`: `/admin/equipment` → `/equipment` (#81), `/admin/events` →
 * `/events` (#82), `/admin/events/:id` → `/events/:id` (#83). The events list
 * links each row to `/events/:id` (the event workspace).
 *
 * There is no standalone home page — the #77 tracer placeholder was removed.
 * The events list is the app home, so `/` redirects there.
 *
 * Every authenticated route is a child of the `AppShell` layout route, which
 * owns the auth gate and the persistent `AppNav` bar. `RequireAuth` used to be
 * repeated per route; it now lives in one place, and global nav is chrome
 * rather than something each page's header has to inject.
 */
export const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    element: <AppShell />,
    children: [
      {
        path: "/",
        element: <Navigate to="/events" replace />,
      },
      {
        path: "/equipment",
        element: <EquipmentPage />,
      },
      {
        // One catalogue item: roles, physical facts, sourcing (#333). The
        // list's inline cells cover name/rate/qty; everything else is edited
        // here.
        path: "/equipment/:id",
        element: <EquipmentDetailPage />,
      },
      {
        path: "/settings",
        element: <SettingsPage />,
      },
      {
        // Xero OAuth redirect target (#91): relays ?code&state back through the
        // authenticated admin API, then returns to Settings → Integrations.
        path: "/settings/xero-callback",
        element: <XeroCallbackPage />,
      },
      {
        path: "/events",
        element: <EventsPage />,
      },
      {
        // The cross-Booking timeline (#303): jobs, equipment, vehicles and
        // crew over a date window.
        path: "/schedule",
        element: <SchedulePage />,
      },
      {
        path: "/events/:id",
        element: <EventWorkspacePage />,
      },
    ],
  },
  {
    // Catch-all: unmatched URLs (incl. the IAP Sign Out path hit in dev)
    // render a friendly 404 instead of React Router's error boundary.
    path: "*",
    element: <NotFound />,
  },
]);
